import {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
} from 'react';
import { FilePreview } from '../FilePreview';
import styles from './FileUploadField.module.css';

export type FileUploadVariant = 'dropzone' | 'button';

export interface FileUploadFieldProps {
  /** File חדש שנבחר, או URL קיים (לעריכת existing), או null */
  value: File | string | null;
  /** file חדש או null במחיקה */
  onChange: (f: File | null) => void;
  /** HTML accept attribute (MIME + extensions) */
  accept?: string;
  /** default 10MB */
  maxSizeBytes?: number;
  /** תווית מעל ה-input */
  label?: string;
  required?: boolean;
  /** אם false — רק dropzone, גם כש-value קיים */
  showPreview?: boolean;
  /** dropzone=big DnD area | button=פשוט יותר */
  variant?: FileUploadVariant;
  /** callback בעת validation failure */
  onError?: (err: string) => void;
  disabled?: boolean;
  className?: string;
}

const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

/** בדיקת validity של file לפי accept + maxSizeBytes. */
const validateFile = (file: File, accept: string, maxSizeBytes: number): string | null => {
  if (file.size > maxSizeBytes) {
    const maxMb = (maxSizeBytes / 1024 / 1024).toFixed(1);
    return `הקובץ גדול מדי (מקס ${maxMb}MB)`;
  }
  if (!accept) return null;
  const accepted = accept.split(',').map((a) => a.trim().toLowerCase());
  const fileType = (file.type || '').toLowerCase();
  const fileName = (file.name || '').toLowerCase();
  const ok = accepted.some((a) => {
    if (a.startsWith('.')) return fileName.endsWith(a);
    if (a.endsWith('/*')) return fileType.startsWith(a.slice(0, -1));
    return fileType === a;
  });
  return ok ? null : 'סוג קובץ לא נתמך';
};

/** Duck typing: התנהגות File-like (אובייקט עם name string) */
type FileLikeValue = { name: string };
const isFileLikeValue = (v: unknown): v is FileLikeValue =>
  !!(v && typeof v === 'object' && typeof (v as { name?: unknown }).name === 'string');

/**
 * שדה העלאת קובץ — drag-and-drop / click-to-select.
 * **Composition:** מציג `<FilePreview>` כש-`value` קיים ו-`showPreview=true`.
 *
 * @example
 * const [file, setFile] = useState(null);
 * <FileUploadField value={file} onChange={setFile} label="חשבונית" required />
 */
export const FileUploadField = ({
  value,
  onChange,
  accept = 'image/*,.pdf',
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  label,
  required = false,
  showPreview = true,
  variant = 'dropzone',
  onError,
  disabled = false,
  className,
}: FileUploadFieldProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isFileLike = isFileLikeValue(value);

  const previewUrl = useMemo<string | null>(() => {
    if (typeof value === 'string') return value;
    if (isFileLike) return URL.createObjectURL(value as File);
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Cleanup blob URLs
  useEffect(
    () => () => {
      if (previewUrl && previewUrl.startsWith && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  const fileName = useMemo<string | undefined>(() => {
    if (typeof value === 'string') {
      const last = value.split('/').pop();
      return last ? last.split('?')[0] : undefined;
    }
    if (isFileLike) return (value as File).name;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const processFile = useCallback(
    (file: File): void => {
      if (!file) return;
      const err = validateFile(file, accept, maxSizeBytes);
      if (err) {
        if (onError) onError(err);
        return;
      }
      onChange(file);
    },
    [accept, maxSizeBytes, onChange, onError]
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const openPicker = (): void => {
    if (!disabled && inputRef.current) inputRef.current.click();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDelete = (): void => {
    if (!disabled) onChange(null);
  };

  const hasValue = value !== null && value !== undefined;
  const showPreviewBlock = hasValue && showPreview && previewUrl;

  return (
    <div
      className={cx(
        styles.root,
        styles[variant],
        disabled && styles.disabled,
        className
      )}
      data-testid="file-upload-field"
    >
      {label && (
        <label className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled}
        required={required}
        className={styles.hiddenInput}
        data-testid="file-upload-input"
        aria-label={label || 'בחירת קובץ'}
      />

      {showPreviewBlock && previewUrl ? (
        <div className={styles.previewWrap}>
          <FilePreview
            fileUrl={previewUrl}
            fileName={fileName}
            variant="thumbnail"
            size="lg"
            onDelete={handleDelete}
          />
          <button
            type="button"
            className={styles.replaceBtn}
            onClick={openPicker}
            disabled={disabled}
            data-testid="file-upload-replace"
          >
            החלף קובץ
          </button>
        </div>
      ) : variant === 'button' ? (
        <button
          type="button"
          className={styles.button}
          onClick={openPicker}
          disabled={disabled}
          aria-required={required || undefined}
          data-testid="file-upload-button"
        >
          <span aria-hidden="true">📤</span>
          <span>בחר קובץ</span>
        </button>
      ) : (
        <div
          className={cx(styles.dropzone, isDragging && styles.dragging)}
          onClick={openPicker}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={label || 'גרור קובץ או לחץ לבחירה'}
          aria-disabled={disabled || undefined}
          aria-required={required || undefined}
          data-testid="file-upload-dropzone"
        >
          <div className={styles.dropzoneIcon} aria-hidden="true">
            📤
          </div>
          <div className={styles.dropzoneTitle}>גרור קובץ או לחץ לבחירה</div>
          <div className={styles.dropzoneHint}>
            {accept} · עד {(maxSizeBytes / 1024 / 1024).toFixed(0)}MB
          </div>
        </div>
      )}
    </div>
  );
};
